import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Edit3, 
  MapPin, 
  Calendar, 
  Heart, 
  Shield, 
  Syringe,
  Users,
  AlertTriangle,
  Camera,
  MessageSquare,
  Clock,
  Star,
  Trash2,
  X
} from 'lucide-react';
import { Strayyo } from '../../types';
import { strayyoService } from '../../services';

interface StrayyoDetailViewProps {
  strayyo: Strayyo;
  onBack: () => void;
  onEdit: () => void;
}

const StrayyoDetailView: React.FC<StrayyoDetailViewProps> = ({ 
  strayyo, 
  onBack, 
  onEdit 
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'angels' | 'emergencies' | 'reports' | 'health'>('overview');
  const [localImages, setLocalImages] = useState(strayyo.images || []);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleDeleteImage = async (imageId: string) => {
    try {
      setDeletingImageId(imageId);
      await strayyoService.deleteImage(strayyo.id, imageId);
      const updated = localImages.filter(img => img.id !== imageId);
      setLocalImages(updated);
      if (activeImageIndex >= updated.length) {
        setActiveImageIndex(Math.max(0, updated.length - 1));
      }
      setDeleteConfirmId(null);
    } catch (err) {
      alert('Failed to delete image. Please try again.');
    } finally {
      setDeletingImageId(null);
    }
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'GOOD': return 'text-green-600 bg-green-100';
      case 'FAIR': return 'text-yellow-600 bg-yellow-100';
      case 'POOR': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'CRITICAL': return 'text-red-600 bg-red-100';
      case 'HIGH': return 'text-orange-600 bg-orange-100';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Heart },
    { id: 'angels', name: 'Angels', icon: Users, count: strayyo.angels?.length },
    { id: 'emergencies', name: 'Emergencies', icon: AlertTriangle, count: strayyo.emergencies?.length },
    { id: 'reports', name: 'Reports', icon: MessageSquare, count: strayyo.publicReports?.length },
    { id: 'health', name: 'Health History', icon: Syringe, count: strayyo.healthStatusHistory?.length },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {strayyo.name || 'Unnamed Strayyo'}
            </h1>
            <p className="text-sm text-gray-500">UID: {strayyo.uid} • {strayyo.type}</p>
          </div>
        </div>
        <button
          onClick={onEdit}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <Edit3 className="h-4 w-4 mr-2" />
          Edit
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Images and Basic Info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Images */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Photos</h3>
              {localImages && localImages.length > 0 ? (
                <div>
                  <div className="aspect-w-1 aspect-h-1 mb-4 relative group">
                    <img
                      src={localImages[activeImageIndex]?.url}
                      alt={strayyo.name || 'Strayyo'}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => setDeleteConfirmId(localImages[activeImageIndex]?.id)}
                      className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                      title="Delete this photo"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {localImages.length > 1 && (
                    <div className="flex space-x-2 overflow-x-auto">
                      {localImages.map((image, index) => (
                        <div key={image.id} className="relative group/thumb flex-shrink-0">
                          <button
                            onClick={() => setActiveImageIndex(index)}
                            className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${
                              index === activeImageIndex ? 'border-orange-500' : 'border-gray-200'
                            }`}
                          >
                            <img
                              src={image.url}
                              alt={`Photo ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(image.id); }}
                            className="absolute -top-1 -right-1 p-0.5 bg-red-600 text-white rounded-full opacity-0 group-hover/thumb:opacity-100 transition-opacity hover:bg-red-700"
                            title="Delete"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Delete confirmation dialog */}
                  {deleteConfirmId && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800 mb-2">Delete this photo? This cannot be undone.</p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDeleteImage(deleteConfirmId)}
                          disabled={deletingImageId !== null}
                          className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          {deletingImageId ? 'Deleting...' : 'Delete'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="px-3 py-1 text-sm bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                  <div className="text-center">
                    <Camera className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No photos available</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Health Status</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getHealthStatusColor(strayyo.healthStatus)}`}>
                    {strayyo.healthStatus.toLowerCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Vaccinated</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    strayyo.vaccinated ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100'
                  }`}>
                    {strayyo.vaccinated ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Sterilized</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    strayyo.sterilized ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100'
                  }`}>
                    {strayyo.sterilized ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Angels</span>
                  <span className="text-gray-900 font-medium">{strayyo.angels?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Last Fed</span>
                  <span className="text-gray-900 text-sm">
                    {strayyo.lastFed ? formatDate(strayyo.lastFed) : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Detailed Information */}
        <div className="lg:col-span-2">
          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'border-orange-500 text-orange-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon className="h-4 w-4 mr-2" />
                        {tab.name}
                        {tab.count !== undefined && (
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                            activeTab === tab.id
                              ? 'bg-orange-100 text-orange-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {tab.count}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {activeTab === 'overview' && (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Name</label>
                        <p className="text-gray-900">{strayyo.name || 'Not set'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Type</label>
                        <p className="text-gray-900">{strayyo.type}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Area</label>
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-gray-900">{strayyo.area || 'Unknown'}</span>
                        </div>
                      </div>
                      {(strayyo.latitude && strayyo.longitude) && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Coordinates</label>
                          <p className="text-gray-900 text-sm">
                            {strayyo.latitude.toFixed(6)}, {strayyo.longitude.toFixed(6)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Timeline</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Created</label>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-gray-900 text-sm">{formatDate(strayyo.createdAt)}</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Last Updated</label>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-gray-900 text-sm">{formatDate(strayyo.updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'angels' && (
              <div className="p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Managing Angels</h4>
                {strayyo.angels && strayyo.angels.length > 0 ? (
                  <div className="space-y-4">
                    {strayyo.angels.map((angelAssignment) => (
                      <div key={angelAssignment.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center">
                              <Users className="h-5 w-5 text-orange-600" />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">
                                {angelAssignment.angel?.user?.name || 'Unknown Angel'}
                              </p>
                              <p className="text-sm text-gray-500">
                                {angelAssignment.angel?.user?.email}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              angelAssignment.role === 'PRIMARY' 
                                ? 'bg-orange-100 text-orange-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {angelAssignment.role}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              Since {formatDate(angelAssignment.createdAt)}
                            </p>
                          </div>
                        </div>
                        {angelAssignment.notes && (
                          <div className="mt-3 text-sm text-gray-600">
                            <strong>Notes:</strong> {angelAssignment.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No angels assigned to this strayyo</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'emergencies' && (
              <div className="p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Emergency Reports</h4>
                {strayyo.emergencies && strayyo.emergencies.length > 0 ? (
                  <div className="space-y-4">
                    {strayyo.emergencies.map((emergency) => (
                      <div key={emergency.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUrgencyColor(emergency.urgency)}`}>
                                {emergency.urgency}
                              </span>
                              <span className="ml-2 text-sm text-gray-500">{emergency.type}</span>
                            </div>
                            <p className="text-gray-900 mb-2">{emergency.description}</p>
                            <div className="text-sm text-gray-500">
                              <p>Reported: {formatDate(emergency.reportedAt)}</p>
                              {emergency.reporterName && (
                                <p>Reporter: {emergency.reporterName}</p>
                              )}
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            emergency.status === 'RESOLVED' ? 'bg-green-100 text-green-800' :
                            emergency.status === 'RESPONDING' ? 'bg-blue-100 text-blue-800' :
                            emergency.status === 'ESCALATED' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {emergency.status}
                          </span>
                        </div>
                        {emergency.responses && emergency.responses.length > 0 && (
                          <div className="mt-4 pl-4 border-l-2 border-gray-200">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Responses</h5>
                            {emergency.responses.map((response) => (
                              <div key={response.id} className="mb-2">
                                <p className="text-sm text-gray-600">
                                  <strong>{response.angel.user?.name}:</strong> {response.notes}
                                </p>
                                <p className="text-xs text-gray-500">{formatDate(response.respondedAt)}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No emergency reports for this strayyo</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Public Reports</h4>
                {strayyo.publicReports && strayyo.publicReports.length > 0 ? (
                  <div className="space-y-4">
                    {strayyo.publicReports.map((report) => (
                      <div key={report.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                {report.reportType.replace('_', ' ')}
                              </span>
                            </div>
                            <p className="text-gray-900 mb-2">{report.description}</p>
                            <div className="text-sm text-gray-500">
                              <p>Reported: {formatDate(report.reportedAt)}</p>
                              {report.reporterName && (
                                <p>Reporter: {report.reporterName}</p>
                              )}
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            report.status === 'RESOLVED' ? 'bg-green-100 text-green-800' :
                            report.status === 'REVIEWED' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {report.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No public reports for this strayyo</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'health' && (
              <div className="p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Health Status History</h4>
                {strayyo.healthStatusHistory && strayyo.healthStatusHistory.length > 0 ? (
                  <div className="space-y-4">
                    {strayyo.healthStatusHistory.map((entry) => (
                      <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getHealthStatusColor(entry.status)}`}>
                              {entry.status.toLowerCase()}
                            </span>
                            <span className="ml-3 text-sm text-gray-900">{entry.notes}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">{formatDate(entry.createdAt)}</p>
                            {entry.angel?.user?.name && (
                              <p className="text-xs text-gray-400">by {entry.angel.user.name}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Syringe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No health status history available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrayyoDetailView;